import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useOrg } from '../context/OrgContext';
import './PublicChannel.css';

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

/**
 * OrgCommunities — browse page for org employees to see all their org's channels.
 * Matches the consumer PublicChannelBrowse look & feel with search, category grouping,
 * expand/collapse, and activity indicators.
 */
function OrgCommunities() {
  const navigate = useNavigate();
  const params = useParams();
  const { orgSlug, orgName } = useOrg();
  const slug = params.slug || orgSlug;
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCategories, setOpenCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchOrgChannels();
  }, []);

  const fetchOrgChannels = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get('/api/channels?orgScope=true&browseAll=true', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = Array.isArray(res.data) ? res.data : res.data.channels || [];
      setChannels(data);
      // Auto-expand all categories
      const expanded = {};
      data.forEach(ch => {
        const key = ch.category_display || 'Other';
        expanded[key] = true;
      });
      setOpenCategories(expanded);
    } catch (err) {
      console.error('Error fetching org channels:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group channels by category
  const categoryGroups = [];
  const groupMap = {};
  channels.forEach(ch => {
    const key = ch.category_display || 'Other';
    if (!groupMap[key]) {
      groupMap[key] = {
        category_display: key,
        category_icon: ch.category_icon || '💬',
        channels: []
      };
      categoryGroups.push(groupMap[key]);
    }
    groupMap[key].channels.push(ch);
  });

  // Client-side search filter
  const term = searchTerm.toLowerCase().trim();
  const filteredGroups = term
    ? categoryGroups
        .map(group => {
          if (group.category_display.toLowerCase().includes(term)) return group;
          const matched = group.channels.filter(ch =>
            ch.name.toLowerCase().includes(term)
          );
          return matched.length > 0 ? { ...group, channels: matched } : null;
        })
        .filter(Boolean)
    : categoryGroups;

  const totalSpaces = channels.length;

  const toggleCategory = (key) => {
    setOpenCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChannelClick = (channel) => {
    const path = slug
      ? `/org/${slug}/channels/${channel.channel_id}`
      : `/channels/${channel.channel_id}`;
    navigate(path);
  };

  return (
    <div>
      <Navbar />
      <div className="pb-browse-container" style={{ maxWidth: '720px', margin: '0 auto', padding: '16px' }}>
        {/* Header */}
        <div className="hp-section-header">
          <h2>🏢 {orgName || 'Organization'} Spaces</h2>
          <p className="hp-section-sub">
            {totalSpaces > 0 && `${totalSpaces} spaces`}
            {totalSpaces === 0 && !loading && 'No spaces yet'}
          </p>
        </div>

        {/* Privacy badge */}
        <div style={{
          backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px',
          padding: '8px 16px', marginBottom: '16px', fontSize: '13px', color: '#166534',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <span>🔒</span>
          <span>Private — only visible to {orgName || 'your org'} employees. All posts are anonymous.</span>
        </div>

        {/* Search bar */}
        {!loading && channels.length > 0 && (
          <div className="hp-search-bar">
            <span className="hp-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search spaces..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="hp-search-input"
              aria-label="Search spaces"
            />
            {searchTerm && (
              <button className="hp-search-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">✕</button>
            )}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="pb-browse-loading">Loading spaces...</div>
        ) : channels.length === 0 ? (
          <div className="pb-browse-empty">
            <p style={{ fontSize: '48px', margin: '0 0 12px' }}>📭</p>
            <p>No spaces available yet. Ask your org admin to enable identity categories.</p>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="pb-browse-empty">No spaces match "{searchTerm}". Try a different term.</div>
        ) : (
          filteredGroups.map(group => {
            const isOpen = openCategories[group.category_display];
            const newPosts = group.channels.reduce((s, c) => s + (c.new_post_count || 0), 0);

            return (
              <div key={group.category_display} className="pb-cat-section">
                <button
                  className="pb-cat-header"
                  onClick={() => toggleCategory(group.category_display)}
                  aria-expanded={isOpen}
                >
                  <div className="pb-cat-left">
                    <span className="pb-cat-icon">{group.category_icon}</span>
                    <div>
                      <div className="pb-cat-title">{group.category_display}</div>
                      <div className="pb-cat-stats">
                        {group.channels.length} {group.channels.length === 1 ? 'space' : 'spaces'}
                        {newPosts > 0 && <span className="pb-cat-dot">·</span>}
                        {newPosts > 0 && <span style={{ color: '#7C3AED', fontWeight: '600' }}>{newPosts} new</span>}
                      </div>
                    </div>
                  </div>
                  <span className={`pb-cat-chevron ${isOpen ? 'open' : ''}`}>›</span>
                </button>

                {isOpen && (
                  <div className="pb-cat-channels">
                    {group.channels.map(ch => (
                      <div
                        key={ch.channel_id}
                        className="pb-ch-card"
                        onClick={() => handleChannelClick(ch)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleChannelClick(ch)}
                        style={{ cursor: 'pointer', textDecoration: 'none' }}
                      >
                        <div className="pb-ch-top">
                          <span className="pb-ch-slug">{ch.name}</span>
                          {ch.new_post_count > 0 && (
                            <span className="pb-ch-badge" style={{ backgroundColor: '#7C3AED', color: '#fff' }}>
                              {ch.new_post_count} new
                            </span>
                          )}
                        </div>
                        <div className="pb-ch-meta">
                          {typeof ch.member_count === 'string' ? ch.member_count : `${ch.member_count} members`}
                          {ch.last_post_at && (
                            <>
                              <span className="pb-cat-dot">·</span>
                              {formatTimeAgo(ch.last_post_at)}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Back to dashboard */}
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button
            onClick={() => navigate(slug ? `/org/${slug}/dashboard` : '/dashboard')}
            style={{
              background: 'none', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '10px 20px', fontSize: '14px', color: '#6b7280', cursor: 'pointer'
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrgCommunities;
